<template>
  <div class="feature-block">
    <div class="feature-block__header">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <CIcon
            name="cipInfo"
            size="sm"
            class="mr-2 feature-block__help"
            v-c-tooltip="{
              content: $pgettext('features.collaborator_limits', 'Limit the number of collaborators that can be invited to this event, optionally per role.'),
              placement: 'top',
              appendToBody: true
            }"
          />
          <span class="feature-block__title" v-translate translate-context="features.collaborator_limits">
            Collaborator Limits
          </span>
        </div>
        <CSwitch
          color="primary"
          variant="3d"
          size="sm"
          :checked="localEnabled"
          @update:checked="toggleEnabled"
        />
      </div>
    </div>

    <div v-if="localEnabled" class="feature-block__content mt-3">
      <!-- Role Limits Section -->
      <div class="mb-4">
        <label class="font-weight-bold d-flex align-items-center mb-2">
          <translate translate-context="features.collaborator_limits">Role Limits</translate>
          <CIcon
            name="cipInfo"
            size="sm"
            class="ml-2 text-muted"
            v-c-tooltip="{
              content: $pgettext('features.collaborator_limits', 'Define maximum number of collaborators per role. Only roles with a limit defined will be available for invitation.'),
              placement: 'top',
              appendToBody: true
            }"
          />
        </label>

        <!-- Role Limits List -->
        <div class="role-limits-list">
          <div
            v-for="(limit, index) in localRoleLimits"
            :key="index"
            class="role-limit-item d-flex align-items-center mb-2"
          >
            <CSelect
              v-model="limit.roleCode"
              :options="getAvailableRolesForSelect(limit.roleCode)"
              :placeholder="$pgettext('features.collaborator_limits', 'Select role')"
              class="role-select mr-2"
              size="sm"
            />
            <CInput
              v-model.number="limit.limit"
              type="number"
              min="0"
              step="1"
              :placeholder="$pgettext('features.collaborator_limits', 'Limit')"
              class="limit-input mr-2"
              size="sm"
            />
            <CButton
              color="danger"
              variant="ghost"
              size="sm"
              @click="removeRoleLimit(index)"
              v-c-tooltip="$pgettext('features.collaborator_limits', 'Remove')"
            >
              <CIcon name="cilTrash" size="sm"/>
            </CButton>
          </div>

          <div v-if="localRoleLimits.length === 0" class="text-muted small mb-2">
            <translate translate-context="features.collaborator_limits">No role limits configured. Add at least one role to enable limits.</translate>
          </div>

          <CButton
            color="primary"
            variant="outline"
            size="sm"
            @click="addRoleLimit"
            :disabled="!hasAvailableRolesToAdd"
          >
            <CIcon name="cilPlus" size="sm" class="mr-1"/>
            <translate translate-context="features.collaborator_limits">Add Role</translate>
          </CButton>
        </div>
      </div>

      <!-- Total Limit Section -->
      <div class="mb-3">
        <label class="font-weight-bold d-flex align-items-center mb-2">
          <translate translate-context="features.collaborator_limits">Total Limit</translate>
          <span class="text-muted font-weight-normal ml-2 small">
            <translate translate-context="features.collaborator_limits">(Optional)</translate>
          </span>
        </label>
        <div class="d-flex align-items-center">
          <CInput
            v-model.number="localTotalLimit"
            type="number"
            min="0"
            step="1"
            :placeholder="$pgettext('features.collaborator_limits', 'No total limit')"
            class="total-limit-input"
            size="sm"
          />
          <CButton
            v-if="localTotalLimit !== null && localTotalLimit !== ''"
            color="light"
            size="sm"
            class="ml-2"
            @click="clearTotalLimit"
            v-c-tooltip="$pgettext('features.collaborator_limits', 'Clear total limit')"
          >
            <CIcon name="cilX" size="sm"/>
          </CButton>
        </div>
        <small class="text-muted">
          <translate translate-context="features.collaborator_limits">Maximum total collaborators regardless of role. Leave empty for no total limit.</translate>
        </small>
      </div>

      <!-- Warning if no roles configured -->
      <div v-if="localEnabled && localRoleLimits.length === 0" class="alert alert-warning small">
        <CIcon name="cilWarning" size="sm" class="mr-1"/>
        <translate translate-context="features.collaborator_limits">Warning: No roles are configured. No one will be able to invite collaborators.</translate>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'FeatureCollaboratorLimits',

  props: {
    config: {
      type: Object,
      default: null
    },
    picaServiceId: {
      type: [Number, String],
      default: null
    }
  },

  data() {
    return {
      localEnabled: false,
      localRoleLimits: [], // [{roleCode: 'photographer', limit: 3}, ...]
      localTotalLimit: null
    }
  },

  computed: {
    ...mapGetters('picaServiceFeatures', ['roles']),

    // Get roles that have a code (required for limits)
    rolesWithCode() {
      return (this.roles || []).filter(role => role.code)
    },

    // Check if there are roles available to add
    hasAvailableRolesToAdd() {
      const usedCodes = this.localRoleLimits.map(item => item.roleCode)
      return this.rolesWithCode.some(role => !usedCodes.includes(role.code))
    }
  },

  watch: {
    config: {
      handler() {
        this.reset()
      },
      immediate: true
    }
  },

  mounted() {
    this.fetchConfigurableRoles(this.picaServiceId)
  },

  methods: {
    ...mapActions('picaServiceFeatures', ['fetchConfigurableRoles']),
    reset() {
      if (!this.config) {
        this.localEnabled = false
        this.localRoleLimits = []
        this.localTotalLimit = null
        return
      }

      // Check if feature is enabled (config exists and has data)
      const hasRoleLimits = this.config.role_limits && Object.keys(this.config.role_limits).length > 0
      const hasTotalLimit = this.config.total_limit !== null && this.config.total_limit !== undefined
      this.localEnabled = hasRoleLimits || hasTotalLimit

      // Parse role_limits object to array
      if (this.config.role_limits) {
        this.localRoleLimits = Object.entries(this.config.role_limits).map(([roleCode, limit]) => ({
          roleCode,
          limit
        }))
      } else {
        this.localRoleLimits = []
      }

      // Set total limit
      this.localTotalLimit = this.config.total_limit ?? null
    },

    getConfig() {
      if (!this.localEnabled) {
        return null
      }

      // Convert array back to object
      const roleLimits = {}
      this.localRoleLimits.forEach(item => {
        if (item.roleCode && item.limit !== null && item.limit !== '' && item.limit >= 0) {
          roleLimits[item.roleCode] = parseInt(item.limit, 10)
        }
      })

      return {
        role_limits: roleLimits,
        total_limit: (this.localTotalLimit !== null && this.localTotalLimit !== '')
          ? parseInt(this.localTotalLimit, 10)
          : null
      }
    },

    toggleEnabled(value) {
      this.localEnabled = value
      if (value && this.localRoleLimits.length === 0) {
        // Add first empty role limit when enabling
        this.addRoleLimit()
      }
    },

    getAvailableRolesForSelect(currentRoleCode) {
      const usedCodes = this.localRoleLimits
        .map(item => item.roleCode)
        .filter(code => code !== currentRoleCode)

      return this.rolesWithCode
        .filter(role => !usedCodes.includes(role.code))
        .map(role => ({
          value: role.code,
          label: role.name || role.code
        }))
    },

    addRoleLimit() {
      // Find first available role
      const usedCodes = this.localRoleLimits.map(item => item.roleCode)
      const availableRole = this.rolesWithCode.find(role => !usedCodes.includes(role.code))

      this.localRoleLimits.push({
        roleCode: availableRole?.code || '',
        limit: 1
      })
    },

    removeRoleLimit(index) {
      this.localRoleLimits.splice(index, 1)
    },

    clearTotalLimit() {
      this.localTotalLimit = null
    }
  }
}
</script>

<style scoped>
.role-limits-list {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
}

.role-limit-item {
  background-color: #fff;
  border-radius: 4px;
  padding: 8px;
}

.role-select {
  flex: 1;
  max-width: 200px;
}

.limit-input {
  width: 80px;
}

.total-limit-input {
  width: 120px;
}
</style>
